import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatCalendar } from '@angular/material/datepicker';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import {
  MunilyDatePickerModalData,
  MunilyDatePickerModalResult,
} from './date-picker-modal.types';

const DEFAULT_TITLE_KEY = 'commonComponents.datePickerModal.title';
const DEFAULT_CLOSE_ARIA_KEY = 'commonComponents.datePickerModal.closeAria';
const DEFAULT_CANCEL_KEY = 'modal.button.cancel';
const DEFAULT_CONFIRM_KEY = 'modal.button.save';

function parseIsoDate(value: string | Date | undefined): Date | null {
  if (!value) return null;
  if (value instanceof Date) {
    const d = new Date(value.getTime());
    d.setHours(0, 0, 0, 0);
    return d;
  }
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value).trim());
  if (!match) return null;
  const d = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  d.setHours(0, 0, 0, 0);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function toIsoDateString(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

@Component({
  selector: 'app-munily-date-picker-modal',
  templateUrl: './date-picker-modal.component.html',
  styleUrls: ['./date-picker-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MunilyDatePickerModalComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild(MatCalendar) private _calendar?: MatCalendar<Date>;

  public selectedDate: Date | null = null;
  public readonly minDate: Date | null;
  public readonly maxDate: Date | null;

  public readonly titleKey: string;
  public readonly closeAriaKey: string;
  public readonly cancelKey: string;
  public readonly confirmKey: string;

  private readonly _isDateDisabled?: (isoDate: string) => boolean;
  private readonly _onMonthChange?: (event: { month: number; year: number }) => void;
  private readonly _destroy$ = new Subject<void>();
  private _lastActiveMonthKey = '';

  constructor(
    private readonly _dialogRef: MatDialogRef<MunilyDatePickerModalComponent, MunilyDatePickerModalResult | undefined>,
    @Inject(MAT_DIALOG_DATA) data: MunilyDatePickerModalData | undefined,
    private readonly _cdr: ChangeDetectorRef,
  ) {
    this.titleKey = data?.titleKey ?? DEFAULT_TITLE_KEY;
    this.closeAriaKey = data?.closeAriaKey ?? DEFAULT_CLOSE_ARIA_KEY;
    this.cancelKey = data?.labelButtonCancelKey ?? DEFAULT_CANCEL_KEY;
    this.confirmKey = data?.labelButtonConfirmKey ?? DEFAULT_CONFIRM_KEY;
    this.minDate = data?.minDate ?? null;
    this.maxDate = data?.maxDate ?? null;
    this.selectedDate = parseIsoDate(data?.initialDate);
    this._isDateDisabled = data?.isDateDisabled;
    this._onMonthChange = data?.onMonthChange;
  }

  ngOnInit(): void {
    if (!this.selectedDate) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      this.selectedDate = today;
    }
  }

  ngAfterViewInit(): void {
    this._calendar?.stateChanges
      .pipe(takeUntil(this._destroy$))
      .subscribe(() => this._notifyMonthChange());

    this._notifyMonthChange();
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  dateFilter = (date: Date | null): boolean => this._evaluateDateFilter(date);

  refreshDateFilter(): void {
    // MatCalendar caches filter results by function identity — reassign so cells re-evaluate
    // after async disabledDates updates (e.g. area reservation calendar).
    this.dateFilter = (date: Date | null): boolean => this._evaluateDateFilter(date);
    this._calendar?.updateTodaysDate();
    this._cdr.markForCheck();
  }

  private _evaluateDateFilter(date: Date | null): boolean {
    if (!date) return false;
    if (!this._isDateDisabled) return true;
    return !this._isDateDisabled(toIsoDateString(date));
  }

  onCalendarSelected(date: Date | null): void {
    this.selectedDate = date;
    this._cdr.markForCheck();
  }

  onClose(): void {
    this._dialogRef.close(undefined);
  }

  onConfirm(): void {
    if (!this.selectedDate) {
      this._dialogRef.close(undefined);
      return;
    }
    this._dialogRef.close({ date: toIsoDateString(this.selectedDate) });
  }

  private _notifyMonthChange(): void {
    const active = this._calendar?.activeDate;
    if (!active || !this._onMonthChange) return;

    const month = active.getMonth() + 1;
    const year = active.getFullYear();
    const key = `${year}-${month}`;
    if (key === this._lastActiveMonthKey) return;

    this._lastActiveMonthKey = key;
    this._onMonthChange({ month, year });
  }
}
